import type { LocaleResolverContext } from '#src/shared/routes';

import { extractTopicLocaleFromSlug } from '#src/app/shared/utils/topic';

import { getTopicPageLocale } from './api/topicPageLocale';

/**
 * Topic pages have an authoritative `locale` (e.g. `ja-JP`). For bots (e.g. Googlebot,
 * which crawls as en-US), we override the request locale with the topic's locale so the
 * SEO meta tags — title and description — are generated in the topic's language instead
 * of English. Real users keep their own locale.
 *
 * We read the locale from GraphQL, falling back to the locale encoded in the slug suffix
 * (e.g. `裏金-ja-jp`) when the lookup returns nothing or fails — a failed lookup should
 * never break bot SEO. Returns `undefined` (no override) for non-bots and when neither
 * source resolves a locale, in which case the user's l10n is used.
 */
export async function resolveTopicL10nForBotsFromSlugParam({
	params: { slug },
	utilities,
	isBot,
}: LocaleResolverContext): Promise<{ locale?: string; countryCode?: string } | undefined> {
	if (!isBot() || !slug) return undefined;

	const locale = (await getTopicPageLocale(slug, utilities).catch(() => undefined)) ?? extractTopicLocaleFromSlug(slug);
	return locale ? { locale } : undefined;
}
